import type { DragEvent } from 'react';
import { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import StartIcon from '@material-ui/icons/Star';
import { FavoriteShortcut, ShortcutLink, ViewMode } from '../../types';
import { EmptyData, ShortcutSectionItem } from '../atoms/';

type Props = {
  viewMode: ViewMode;
  favorites: FavoriteShortcut[];
  hasAnyFavorites: boolean;
  favoritesLoaded: boolean;
  searchQuery: string;
  toggleFavorite: (link: ShortcutLink) => void;
  isReorderingFavorites?: boolean;
  onReorderFavorites?: (links: ShortcutLink[]) => void;
};

export const FavoriteSection = ({
  viewMode,
  favorites,
  hasAnyFavorites,
  favoritesLoaded,
  searchQuery,
  toggleFavorite,
  isReorderingFavorites = false,
  onReorderFavorites,
}: Props) => {
  const classes = useStyles();
  const [draggedLinkId, setDraggedLinkId] = useState<string | undefined>();
  const [dragStartKey, setDragStartKey] = useState<string | undefined>();

  const favoriteListKey = useMemo(
    () => favorites.map(({ link }) => link.id).join('|'),
    [favorites],
  );
  const canRenderReorderControls =
    searchQuery.trim().length === 0 &&
    favorites.length > 1 &&
    Boolean(onReorderFavorites);
  const canDragFavorites = canRenderReorderControls && !isReorderingFavorites;

  const handleDragStart = (
    event: DragEvent<HTMLDivElement>,
    linkId: string,
  ) => {
    if (!canDragFavorites) {
      return;
    }

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', linkId);
    setDraggedLinkId(linkId);
    setDragStartKey(favoriteListKey);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!canDragFavorites) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (
    event: DragEvent<HTMLDivElement>,
    targetLinkId: string,
  ) => {
    event.preventDefault();

    const sourceLinkId =
      draggedLinkId || event.dataTransfer.getData('text/plain');
    setDraggedLinkId(undefined);
    setDragStartKey(undefined);

    if (
      !canDragFavorites ||
      !sourceLinkId ||
      sourceLinkId === targetLinkId ||
      dragStartKey !== favoriteListKey
    ) {
      return;
    }

    const sourceIndex = favorites.findIndex(
      ({ link }) => link.id === sourceLinkId,
    );
    const targetIndex = favorites.findIndex(
      ({ link }) => link.id === targetLinkId,
    );

    if (sourceIndex === -1 || targetIndex === -1) {
      return;
    }

    const reordered = [...favorites];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    onReorderFavorites?.(reordered.map(({ link }) => link));
  };

  const handleDragEnd = () => {
    setDraggedLinkId(undefined);
    setDragStartKey(undefined);
  };

  const renderItems = (() => {
    if (!favoritesLoaded) {
      return <EmptyData title="Loading favorites..." body=" " />;
    }

    if (!hasAnyFavorites) {
      return (
        <EmptyData
          title="No favorited shortcuts yet."
          body="Favorite your most-used tools to keep them available at the top."
        />
      );
    }

    if (favorites.length === 0) {
      return (
        <EmptyData
          title={`No favorite shortcuts found for "${searchQuery}".`}
          body="Try another search term or clear the search to see all your favorites."
        />
      );
    }

    const isListMode = viewMode === ViewMode.LIST;

    if (canRenderReorderControls) {
      return (
        <Box className={isListMode ? classes.list : classes.grid}>
          {favorites.map(({ link }) => {
            const isDragging = draggedLinkId === link.id;

            return (
              <Box
                key={link.id}
                className={`${classes.draggableItem} ${
                  isDragging ? classes.draggableRowDragging : ''
                }`}
                draggable={canDragFavorites}
                onDragStart={event => handleDragStart(event, link.id)}
                onDragOver={handleDragOver}
                onDrop={event => handleDrop(event, link.id)}
                onDragEnd={handleDragEnd}
                data-testid={`favorite-shortcut-${link.id}`}
              >
                <Tooltip title="Arrastra para reordenar">
                  <span>
                    <IconButton
                      className={classes.dragHandle}
                      size="small"
                      aria-label={`Reorder ${link.name}`}
                      disabled={!canDragFavorites}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Box flex={1} minWidth={0}>
                  <ShortcutSectionItem
                    link={link}
                    isFavorite
                    toggleFavorite={toggleFavorite}
                    actionsDisabled={isReorderingFavorites}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      );
    }

    return (
      <Box className={isListMode ? classes.list : classes.grid}>
        {favorites.map(({ link }) => (
          <ShortcutSectionItem
            key={link.id}
            link={link}
            isFavorite
            toggleFavorite={toggleFavorite}
            actionsDisabled={isReorderingFavorites}
          />
        ))}
      </Box>
    );
  })();

  return (
    <Box className={classes.section}>
      <Box className={classes.sectionHeader}>
        <Box className={classes.sectionTitleWrap}>
          <StartIcon className={classes.sectionIcon} />
          <Typography variant="h4" className={classes.sectionTitle}>
            Favorites
          </Typography>
        </Box>
        {isReorderingFavorites && (
          <CircularProgress
            size={20}
            aria-label="Guardando orden de favoritos"
          />
        )}
      </Box>

      {renderItems}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  list: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  draggableItem: {
    display: 'flex',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  draggableRowDragging: {
    opacity: 0.6,
  },
  dragHandle: {
    alignSelf: 'center',
    color: theme.palette.text.secondary,
    cursor: 'grab',
    marginTop: theme.spacing(1.5),
    '&:disabled': {
      cursor: 'default',
    },
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  sectionTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  sectionIcon: {
    color: theme.palette.secondary.main,
    flexShrink: 0,
  },
  sectionTitle: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: '1.8rem',
    lineHeight: 1.25,
  },
}));
